import { useInfiniteQuery } from '@tanstack/react-query';
import type { ComponentType, LazyExoticComponent } from 'react';
import { lazy, useCallback, useEffect, useState } from 'react';

import { useRelevancyOptions } from './store';
import type { RelevancyCard } from './types';
import { RelevancyCardType } from './types';

import { loadEntitiesByClassification, loadWebContainerEntities } from '@apis/editorial';
import { useUserPreferences } from '@shared/hooks/useUserPreferences';
import { IS_DEBUG, language } from '@utilities/browser';
import { orderBy } from '@utilities/collection-utils';
import { loadFromAPIs } from '@utilities/fetch';
import { Trace } from '@utilities/logging';
import { apiKey, competitionId } from '@utilities/WindowContext';

type CustomCard = {
  index: number;
  type: RelevancyCardType;
  subType?: string;
  teaser?: any;
  section?: string;
};

const _checkComponentExists = (type: RelevancyCardType, Components: { [key: string]: string }) => {
  if (!type) return false;

  const result = type in Components;

  if (!result) Trace(`Component ${type} doesn't exists`);

  return result || IS_DEBUG;
};

export const getComponentName = (type: RelevancyCardType, Components: { [key: string]: string }): string => {
  if (!_checkComponentExists(type, Components)) return null;

  return Components[type];
};

export const getComponent = <T = any>(
  type: RelevancyCardType,
  Components: { [key: string]: string },
): LazyExoticComponent<ComponentType<T>> => {
  const name = getComponentName(type, Components);
  if (!name) return null;
  return lazy<ComponentType<T>>(() => import(/* webpackPrefetch: true */ `./components/${name}`));
};

export const useRelevancyFeed = () => {
  const { options } = useRelevancyOptions();

  const {
    Components,
    competitionCode,
    cardsNumber,
    relevancy_version,
    relevanceApiUrl,
    testRelevanceUrl,
    excludedIds,
  } = options;

  const [feed, setFeed] = useState<RelevancyCard[]>([]);
  const setCardError = useCallback(
    (card: RelevancyCard, error?: any, info?: any) =>
      setFeed((prev) =>
        prev.map((item) => {
          if (item === card || (item?.teaser?.id && card?.teaser?.id && item.teaser.id === card.teaser.id)) {
            return { ...item, error, info };
          }
          return item;
        }),
      ),
    [],
  );

  const toggleCardHidden = useCallback(
    (card: RelevancyCard, hidden = true) =>
      setFeed((prev) =>
        prev.map((item) => {
          if (item === card || (item?.teaser?.id && card?.teaser?.id && item.teaser.id === card.teaser.id)) {
            return { ...item, hidden };
          }
          return item;
        }),
      ),
    [],
  );

  const injectCustomCard = useCallback(
    (currentFeed: RelevancyCard[], { index, type, subType, teaser }: CustomCard) => {
      currentFeed.splice(index - 1, 0, {
        teaser,
        competitionCode: competitionCode.toUpperCase(),
        type,
        subType,
      } as RelevancyCard);
    },
    [competitionCode],
  );

  const prepareFeed = useCallback(
    (newFeed: RelevancyCard[]) => {
      if (!newFeed) return;

      newFeed = newFeed
        .filter((item) => item.competitionCode === competitionCode.toUpperCase())
        .filter(({ teaser }) => !excludedIds?.length || !teaser?.id || !excludedIds.includes(teaser.id))
        .flatMap((item) => item.subCards?.filter((c) => c.type !== RelevancyCardType.EIDOS_CONTENT) ?? item)
        .filter((item) => _checkComponentExists(item.type, Components));

      let additionalCards: CustomCard[] = [];

      if (options.audioCardIndex) {
        additionalCards.push({ index: options.audioCardIndex, type: RelevancyCardType.LIVE_AUDIO });
      }

      additionalCards = orderBy(additionalCards, 'index');

      additionalCards.forEach((card) => injectCustomCard(newFeed, card));

      return newFeed;
    },
    [options.audioCardIndex, competitionCode, excludedIds, Components, injectCustomCard],
  );

  const mapToRelevancyCard = useCallback(
    (node: any): RelevancyCard => ({
      cardId: node.id,
      teaser: node,
      competitionCode: competitionCode.toUpperCase(),
      type: RelevancyCardType.EIDOS_CONTENT,
    }),
    [competitionCode],
  );

  const loadCardsFromEidos = useCallback(
    async (skip = options.skip ?? 0) => {
      let wcResult = [];
      if (options.webContainer) {
        wcResult = await loadWebContainerEntities(options.webContainer);

        wcResult = wcResult.filter((node) => !excludedIds?.length || !node?.id || !excludedIds.includes(node.id));

        if (wcResult.length >= cardsNumber || !options.kinds) {
          return prepareFeed(wcResult.map(mapToRelevancyCard));
        }
      }

      const filters = {
        type: options.mediaTypes,
        ['param.attributes.main.competition']: competitionId,
        ['param.attributes.main.kind']: options.kinds,
        ['param.attributes.sections']: options.sections,
        ['param.attributes.hashtags']: options.hashtags,
        ['param.attributes.main.season']: options.season,
        ['param.attributes.main.phase']: options.phase,
        ['param.attributes.workFolder']: options.workFolder,
        startDate: options.startDate,
        endDate: options.endDate,
        ['param.attributes.footballEntities.team.teamId']: options.teamId,
        ['param.attributes.footballEntities.player.playerId']: options.playerId,
        ['param.attributes.footballEntities.association.associationId']: options.associationId,
        ['param.attributes.footballEntities.venue.venueId']: options.venueId,
        limit: cardsNumber + 10 - wcResult.length + (excludedIds?.length ?? 0),
        offset: skip,
        sorting: '-attributes.firstPublicationDate',
      };

      let result = await loadEntitiesByClassification(filters);
      result = result.filter((node) => !excludedIds?.length || !node?.id || !excludedIds.includes(node.id));
      return prepareFeed([...wcResult, ...result].map(mapToRelevancyCard));
    },
    [
      options.skip,
      options.webContainer,
      options.mediaTypes,
      options.kinds,
      options.sections,
      options.hashtags,
      options.season,
      options.phase,
      options.workFolder,
      options.startDate,
      options.endDate,
      options.teamId,
      options.playerId,
      options.associationId,
      options.venueId,
      cardsNumber,
      excludedIds,
      prepareFeed,
      mapToRelevancyCard,
    ],
  );

  const { favouriteTeam } = useUserPreferences();
  const loadCardsFromRelevancy = useCallback(async () => {
    const limit = cardsNumber + 10 + (excludedIds?.length ?? 0);
    const filters = {
      limit,
      favouriteteam: favouriteTeam?.id,
      clientId: 'UEFA_COM_WEB',
      language: language.toUpperCase(),
    };

    const url = `v${relevancy_version}/competition-cards/${competitionCode.toUpperCase()}`;

    const newFeed = testRelevanceUrl
      ? await loadFromAPIs<RelevancyCard[]>(testRelevanceUrl)
      : await loadFromAPIs<RelevancyCard[]>(url, filters, { apiKey, apiUrl: relevanceApiUrl });
    return prepareFeed(newFeed);
  }, [
    cardsNumber,
    competitionCode,
    excludedIds?.length,
    favouriteTeam?.id,
    prepareFeed,
    relevanceApiUrl,
    relevancy_version,
    testRelevanceUrl,
  ]);

  const { data, isLoading, fetchNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['relevancyFeed', options, favouriteTeam],
    queryFn: ({ pageParam = 0 }) => {
      if (options.webContainer || options.useEidos) {
        return loadCardsFromEidos(pageParam);
      }

      return loadCardsFromRelevancy();
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      options.useEidos && options.infiniteScroll && lastPage.length ? allPages.flatMap((p) => p).length : undefined,
    select: (data) => data?.pages.flatMap((d) => d) ?? [],
  });

  useEffect(() => {
    if (data) setFeed(data);
  }, [data]);

  return {
    loadMore: fetchNextPage,
    feed,
    isLoading: isLoading || isFetchingNextPage,
    setCardError,
    toggleCardHidden,
  };
};
